// calibrationLogBookPatchQueue.js
// IndexedDB queue management for Calibration Log Book autosave - holds websocket patches for offline.

import { openDB } from "idb";
import { withIdbRetry } from "./idbRetry";

const DB_NAME = "calibrationLogBookPatchQueue";
const STORE_NAME = "patches";

const getDb = async () => {
  return openDB(DB_NAME, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, {
          keyPath: "queueId",
          autoIncrement: true,
        });
        store.createIndex("bookId", "bookId", { unique: false });
      }
    },
  });
};

export const queuePatch = async (bookId, patch) => {
  return withIdbRetry(
    async () => {
      const db = await getDb();
      return db.add(STORE_NAME, {
        bookId,
        patch,
        timestamp: Date.now(),
      });
    },
    undefined,
    "calibrationLogBookPatchQueue.queuePatch"
  );
};

export const getQueuedPatches = async (bookId) => {
  return withIdbRetry(
    async () => {
      const db = await getDb();
      const tx = db.transaction(STORE_NAME, "readonly");
      const index = tx.store.index("bookId");
      return index.getAll(bookId);
    },
    [],
    "calibrationLogBookPatchQueue.getQueuedPatches"
  );
};

export const removePatchFromQueue = async (queueId) => {
  return withIdbRetry(
    async () => {
      const db = await getDb();
      await db.delete(STORE_NAME, queueId);
    },
    undefined,
    "calibrationLogBookPatchQueue.removePatchFromQueue"
  );
};

export const getPendingCount = async (bookId) => {
  const patches = await getQueuedPatches(bookId);
  return patches.length;
};

export const flushQueue = async (bookId, emitFn) => {
  const patches = await getQueuedPatches(bookId);
  let successCount = 0;
  for (const item of patches) {
    const result = await emitFn(bookId, item.patch);
    // Remove from queue if: success OR server rejected it (validation error)
    // Only keep in queue if it should be retried (network issues)
    if (!result.error || !result.shouldQueue) {
      await removePatchFromQueue(item.queueId);
      if (!result.error) {
        successCount++;
      }
    }
  }
  return successCount;
};

export const clearQueue = async (bookId) => {
  return withIdbRetry(
    async () => {
      const db = await getDb();
      const tx = db.transaction(STORE_NAME, "readwrite");
      const index = tx.store.index("bookId");
      const keys = await index.getAllKeys(bookId);
      for (const key of keys) {
        await tx.store.delete(key);
      }
      await tx.done;
    },
    undefined,
    "calibrationLogBookPatchQueue.clearQueue"
  );
};
